import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, Text, Pressable, TextInput, FlexStyle, Platform, StyleSheet, ScrollView,
} from 'react-native';
import { useSelector } from 'react-redux';
import { TemplateField, UserPermission } from '../../../../../../constants/Types';
import PressableStyled from '../../../../../PressableStyled';
import DeleteIcon24 from '../../../../../24pxIcons/DeleteIcon24';
import Style from '../../../../../../constants/Style';
import DownArrow24 from '../../../../../24pxIcons/DownArrow24';
import SelectionModal from '../../../../../SelectionModal';
import isMobile from '../../../../../IsMobile';
import Colors from '../../../../../../constants/Colors';
import nameRegex from '../../../../../Regex';

const maxNameLength: number = 50;

// eslint-disable-next-line no-shadow
export enum ADDITIONAL_FIELD_TYPES {
  // eslint-disable-next-line no-unused-vars
  STANDARD = '^.*$',
  // eslint-disable-next-line no-unused-vars
  IP = '^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$',
  // eslint-disable-next-line no-unused-vars
  MAC = '^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$',
  // eslint-disable-next-line no-unused-vars
  NUMERIC = '^\\d*$',
  // eslint-disable-next-line no-unused-vars
  DATE = '^\\d\\d\\d\\d-\\d\\d-\\d\\dT\\d\\d:\\d\\d:\\d\\d.\\d\\d\\d\\Z$',
  // eslint-disable-next-line no-unused-vars
  LINK = '[(http(s)?):\\/\\/(www\\.)?a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)',
  // eslint-disable-next-line no-unused-vars
  PORT = '^\\d*\\/?\\d*$',
}

export type AdditionalFieldType = {
  name: string,
  format: ADDITIONAL_FIELD_TYPES,
  description: string,
  placeholder: string,
  invalidMessage: string,
  testID: string,
}

export const ADDITIONAL_FIELD_TYPE_DETAILS: AdditionalFieldType[] = [
  {
    name: 'Plain text',
    format: ADDITIONAL_FIELD_TYPES.STANDARD,
    description: 'Standard input',
    placeholder: 'Enter',
    invalidMessage: 'Invalid value',
    testID: 'additional-standard-input-field',
  },
  {
    name: 'Link',
    format: ADDITIONAL_FIELD_TYPES.LINK,
    description: 'URL',
    placeholder: 'http://www.hereworks.ie',
    invalidMessage: 'Value must be a URL',
    testID: 'additional-url-input-field',
  },
  {
    name: 'IPv4 Address',
    format: ADDITIONAL_FIELD_TYPES.IP,
    description: 'IPv4 address input',
    placeholder: '000.000.000.000',
    invalidMessage: 'Value must be an IP address',
    testID: 'additional-ip-input-field',
  },
  {
    name: 'MAC Address',
    format: ADDITIONAL_FIELD_TYPES.MAC,
    description: 'MAC address input',
    placeholder: 'A0 - 00 - 00 - 00',
    invalidMessage: 'Value must be a MAC address',
    testID: 'additional-mac-input-field',
  },
  {
    name: 'Number',
    format: ADDITIONAL_FIELD_TYPES.NUMERIC,
    description: 'Numeric input',
    placeholder: '000',
    invalidMessage: 'Value can only consist of numeric digits',
    testID: 'additional-number-input-field',
  },
  {
    name: 'Date',
    format: ADDITIONAL_FIELD_TYPES.DATE,
    description: 'Date input (DD/MM/YYYY)',
    placeholder: 'DD / MM / YYYY',
    invalidMessage: 'Value must be a date in the DD/MM/YYYY format',
    testID: 'additional-date-input-field',
  },
  {
    name: 'Port',
    format: ADDITIONAL_FIELD_TYPES.PORT,
    description: 'Numeric input which can contain slashes',
    placeholder: '8080',
    invalidMessage: 'Value can only contain numeric digits and slashes',
    testID: 'additional-port-input-field',
  },
];

interface FieldItemProps {
  field: TemplateField,
  index: number,
  updateName: Dispatch<SetStateAction<string>>,
  updateFormat: Dispatch<SetStateAction<string>>,
  onFocus: () => void,
  toggleMenu: () => void,
  removeField: () => void,
  fieldRefs: TextInput[],
  style: FlexStyle,
  typesShown: boolean,
  hideTypesMenu: () => void,
}

const FieldItem = ({
  field, index, updateName, updateFormat, onFocus, toggleMenu, removeField, fieldRefs, style,
  typesShown: isVisible, hideTypesMenu: hide,
}: FieldItemProps) => {
  const value: string = field.Name;
  const [textFieldFocused, setTextFieldFocused] = useState<boolean>(false);

  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);

  const List = () => <>
    {ADDITIONAL_FIELD_TYPE_DETAILS.map((type: AdditionalFieldType) => <PressableStyled
      key={`field_type_${type.name}`}
      onPress={() => {
        updateFormat(type.format);
        onFocus();
      }}
      style={styles.typeItem}
      disabled={!canEditTemplates}
    >
      <Text style={styles.text}>
        {type.name}
      </Text>
    </PressableStyled>)}
  </>;

  return <View {...{ style }}>
    <View style={Style.row}>
      <Text style={styles.fieldLabel}>{`Additional field ${index + 1}`}</Text>
      <PressableStyled
        onPress={removeField}
        testID='am-additional-field-delete-btn'
        disabled={!canEditTemplates}
      >
        <DeleteIcon24 />
      </PressableStyled>
    </View>
    <View style={[Style.row, styles.mainSection]}>
      <TextInput
        maxLength={maxNameLength}
        onFocus={() => {
          setTextFieldFocused(true);
          onFocus();
        }}
        onBlur={() => setTextFieldFocused(false)}
        value={field.Name}
        onChangeText={updateName}
        ref={(ref) => { fieldRefs[index] = ref; }}
        onEndEditing={() => updateName(value)}
        style={[
          styles.border, styles.text, styles.textInput,
          textFieldFocused && styles.textInputFocused,
          !value?.length && { borderColor: styles.errorText.color },
        ]}
        testID={`additional-field-input#${index + 1}`}
        editable={canEditTemplates}
      />
      <View style={styles.dropdownSection}>
        <Pressable
          onPress={toggleMenu}
          style={[Style.row, styles.typeIndicator, styles.border]}
          testID='am-additional-field-type-dropdown-menu'
          disabled={!canEditTemplates}
        >
          <Text style={[styles.text, { flex: 1 }]}>
            {(ADDITIONAL_FIELD_TYPE_DETAILS
              .filter((type: AdditionalFieldType) => type.format === field.Format)[0]
              ?? ADDITIONAL_FIELD_TYPE_DETAILS[0]).name}
          </Text>
          {canEditTemplates && <DownArrow24
            color={Colors.light.hereworksBlue200}
            style={isVisible ? Style.inverted : Style.upright}
          />}
        </Pressable>
        {isMobile ? <SelectionModal
          listContent={<List />}
          title='Select field type'
          {...{ hide, isVisible }}
        /> : <View>
          {isVisible && <ScrollView
            style={[styles.typeDropdown, styles.border]}
            nestedScrollEnabled
          >
            <List />
          </ScrollView>}
        </View>}
      </View>
    </View>
    {!(value.trim()?.length > 0)
      && <Text style={styles.errorText}>{'Field name cannot be empty'}</Text>}
    {value.trim()?.length > 0 && !nameRegex.exp.test(value.trim())
      && <Text style={styles.errorText}>{`Field name${nameRegex.message}`}</Text>}
    {value.trim()?.length > maxNameLength
      && <Text style={styles.errorText}>
        {`Field name cannot exceed ${maxNameLength} characters`}
      </Text>}
  </View>;
};

const styles = StyleSheet.create({
  text: {
    paddingVertical: 9,
    paddingHorizontal: 12,
    fontSize: 14,
    lineHeight: Platform.OS === 'ios' ? undefined : 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  mainSection: {
    marginBottom: 16,
    alignItems: 'flex-start',
  },
  fieldLabel: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  textInput: {
    flex: 1,
    marginEnd: 8,
    paddingTop: 9,
    height: 40,
    borderWidth: 1,
    outlineWidth: 0,
  },
  border: {
    borderWidth: 1.5,
    borderRadius: 10,
    borderColor: Colors.light.hereworksBlue100,
  },
  errorText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
    marginTop: -12,
    marginBottom: 12,
  },
  typeIndicator: {
    paddingEnd: 12,
    minHeight: 40,
    maxHeight: 80,
  },
  typeDropdown: {
    alignSelf: 'stretch',
    maxHeight: 180,
    top: Platform.OS === 'android' ? undefined : 4,
    marginTop: Platform.OS === 'android' ? 8 : undefined,
    position: Platform.OS === 'android' ? 'relative' : 'absolute',
    width: '100%',
    backgroundColor: '#FFF',
  },
  dropdownSection: {
    flex: 0.7,
    minWidth: 40,
  },
  typeItem: {
    borderRadius: 6,
  },
  textInputFocused: {
    borderColor: Colors.light.hereworksBlue400,
  },
});

export default FieldItem;
